import React, { useEffect, useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { OfficeId } from '../../lib/types';
import { OFFICES } from '../../lib/config';
import { apiListTasks } from '../../lib/api';

interface OfficesGridProps {
  onSelectOffice: (id: OfficeId) => void;
}

/**
 * OfficesGrid — displays the five KAELO office cards.
 * Each card shows the office name, color stripe, active task count, blocked task count.
 */
export default function OfficesGrid({ onSelectOffice }: OfficesGridProps) {
  const [taskCounts, setTaskCounts] = useState<Record<string, { active: number; blocked: number }>>({});

  useEffect(() => {
    apiListTasks().then((tasks) => {
      const counts: Record<string, { active: number; blocked: number }> = {};
      OFFICES.forEach((o) => {
        const officeTasks = tasks.filter((t) => t.office_id === o.id);
        counts[o.id] = {
          active: officeTasks.filter((t) => t.column === 'inprogress').length,
          blocked: officeTasks.filter((t) => t.column === 'review').length,
        };
      });
      setTaskCounts(counts);
    }).catch(() => {});
  }, []);

  return (
    <div className="h-full flex flex-col" data-testid="offices-grid">
      <div className="flex-1 flex items-start md:items-center justify-center p-4 md:p-8 overflow-y-auto">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-5 w-full max-w-4xl">
          {OFFICES.map((office) => {
            const counts = taskCounts[office.id] || { active: 0, blocked: 0 };
            return (
              <button
                key={office.id}
                onClick={() => onSelectOffice(office.id)}
                className="group relative bg-white border border-gray-100 rounded-xl shadow-sm hover:shadow-md transition-all duration-150 overflow-hidden text-left hover:-translate-y-0.5 active:scale-[0.99]"
                data-testid={`office-card-${office.id}`}
              >
                {/* Color stripe */}
                <div
                  className="h-1.5 w-full"
                  style={{ backgroundColor: office.color }}
                />

                <div className="p-5">
                  {/* Office name + arrow */}
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2.5">
                      <div
                        className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-sm font-bold shrink-0"
                        style={{ backgroundColor: office.color }}
                      >
                        {office.name.charAt(0)}
                      </div>
                      <span className="text-base font-semibold text-gray-900">{office.name}</span>
                    </div>
                    <ChevronRight
                      size={16}
                      className="text-gray-300 group-hover:text-gray-500 transition-colors"
                    />
                  </div>

                  {/* Description */}
                  <p className="text-xs text-gray-500 mb-4 leading-relaxed">{office.description}</p>

                  {/* Task counts */}
                  <div className="flex gap-3">
                    <div className="flex flex-col">
                      <span className="text-lg font-bold text-gray-900">{counts.active}</span>
                      <span className="text-[10px] text-gray-400 font-medium uppercase tracking-wider">Active</span>
                    </div>
                    <div className="w-px bg-gray-100" />
                    <div className="flex flex-col">
                      <span className="text-lg font-bold" style={{ color: counts.blocked > 0 ? '#E74C3C' : '#111827' }}>
                        {counts.blocked}
                      </span>
                      <span className="text-[10px] text-gray-400 font-medium uppercase tracking-wider">In Review</span>
                    </div>
                  </div>
                </div>
              </button>
            );
          })}

          {/* KAELO HQ card */}
          <div
            className="relative bg-gray-50 border border-gray-200 rounded-xl p-5 text-left opacity-80"
          >
            <div className="h-1.5 w-full mb-4" style={{ backgroundColor: '#BDC3C7' }} />
            <div className="flex items-center gap-2.5 mb-2">
              <div
                className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-sm font-bold"
                style={{ backgroundColor: '#BDC3C7' }}
              >
                K
              </div>
              <span className="text-base font-semibold text-gray-700">KAELO</span>
            </div>
            <p className="text-xs text-gray-400">Orchestrator — this HQ</p>
          </div>
        </div>
      </div>
    </div>
  );
}
